// ── useTr — translation hook with DB override support ────────────────────
// Priority: 1. DB translations (from Supabase)  2. bundled i18n.js fallback
// DB translations are loaded once per session and cached in memory.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { getEffectiveTranslations } from './i18n';

// Module-level cache — shared across all hook instances
const _cache = { en: null, fr: null };
let _loading = false;
const _listeners = new Set();

async function loadDbTranslations(lang, supabaseClient) {
  if (_cache[lang]) return _cache[lang];
  if (_loading) return null;
  _loading = true;
  try {
    const { data, error } = await supabaseClient
      .from('translations')
      .select('key, value')
      .eq('lang', lang);
    if (error || !data?.length) return null;
    const map = {};
    data.forEach(({ key, value }) => { map[key] = value; });
    _cache[lang] = map;
    _listeners.forEach(fn => fn());
    return map;
  } catch {
    return null;
  } finally {
    _loading = false;
  }
}

export function useTr() {
  const { lang } = useApp();
  const [dbTr, setDbTr] = useState(_cache[lang] || null);
  const bundled = getEffectiveTranslations(lang);
  const triedRef = useRef(false);

  useEffect(() => {
    if (_cache[lang]) { setDbTr(_cache[lang]); return; }
    if (triedRef.current) return;
    triedRef.current = true;

    // Lazy-load supabase to avoid circular deps
    import('./supabase').then(({ supabase }) => {
      if (!supabase) return; // mock mode
      loadDbTranslations(lang, supabase).then(map => {
        if (map) setDbTr(map);
      });
    }).catch(() => {});

    const notify = () => setDbTr(_cache[lang] || null);
    _listeners.add(notify);
    return () => _listeners.delete(notify);
  }, [lang]);

  // Invalidate when lang switches
  useEffect(() => {
    setDbTr(_cache[lang] || null);
    triedRef.current = false;
  }, [lang]);

  return useCallback((key, vars) => {
    // DB override first, then bundled fallback
    let str = (dbTr && dbTr[key]) || bundled[key] || key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      });
    }
    return str;
  }, [dbTr, bundled]);
}
